/**
 * Shared pricing-math helpers for the Scoping "Pricing" sub-tab (ProjectWorkspace.jsx)
 * and the pre-save contract preview (buildContractPreviewFromProfile.js).
 *
 * Money storage convention (CLAUDE.md §6 / §20.5 — INR default, never Float): this table
 * stores rupee amounts as whole-rupee strings formatted with `toLocaleString("en-IN")`
 * (e.g. "36,000"), rounded via `Math.round`. That is the pre-existing convention across
 * this file (see `formatInrForStorage` in ProjectWorkspace.jsx) — matched here rather than
 * introduced fresh, and never represented as a JS `Float` in arithmetic (every value is
 * parsed to an integer number of rupees before use).
 *
 * Bug fix (Task #8 / #9 — pricing/contract calculation bugs):
 *   #8 — a row's "Total" column was a free-editable field, independent of Unit Price and
 *        Total Units, so nothing kept `totalPrice === unitPrice * totalUnits`.
 *   #9 — the Payment Tranches table's `payableAmount` values were a second, independently
 *        edited field with no programmatic link to the Price Estimate grand total, so the
 *        two tables' footer "Total" rows only matched by coincidence in the seed/mock data.
 * Fix: `computeLineTotal` derives a row's Total from Unit Price x Total Units (#8), and
 * `recomputeTranchePayables` derives every tranche's payable amount from the Price
 * Estimate grand total x that tranche's percentage, plugging the rounding remainder into
 * the last tranche so the two tables' totals reconcile exactly, by construction (#9).
 */

/** True when a money / quantity field has no value at all (null, undefined, "" or whitespace). */
export function isBlankMoney(s) {
  return s === null || s === undefined || String(s).trim() === "";
}

/** Parses a possibly comma-formatted numeric string (money or a plain quantity) to a number. */
export function parseMoneyToNumber(s) {
  return Number(String(s ?? "").replace(/,/g, "")) || 0;
}

/** Formats a whole-rupee amount for storage/display, matching ProjectWorkspace's `formatInrForStorage`. */
export function formatInrForStorage(n) {
  const r = Math.round(Number(n) || 0);
  return r.toLocaleString("en-IN");
}

/**
 * A price-estimate row's Total, derived from Unit Price x Total Units — never independently
 * editable. Rounded to the nearest whole rupee (this codebase's existing money convention).
 */
export function computeLineTotal(totalUnits, unitPrice) {
  // A line with no quantity or no rate has no total: blank, never "0" (user ruling: absent is
  // acceptable, fake is not — a 0 would read as a real free line in the contract).
  if (isBlankMoney(totalUnits) || isBlankMoney(unitPrice)) return "";
  const qty = parseMoneyToNumber(totalUnits);
  const price = parseMoneyToNumber(unitPrice);
  return formatInrForStorage(qty * price);
}

/**
 * Sums a Price Estimate table's row totals into a grand total (a plain number of rupees).
 * Mirrors the existing footer-sum rule: a category row's own total counts only when it has
 * no children (its children's totals are summed instead, so a parent's blank total field
 * is never double-counted).
 */
export function computePriceEstimateGrandTotal(rows) {
  let total = 0;
  (rows || []).forEach((pe) => {
    if (pe.children && pe.children.length) {
      pe.children.forEach((c) => {
        total += parseMoneyToNumber(c.totalPrice);
      });
    } else {
      total += parseMoneyToNumber(pe.totalPrice);
    }
  });
  return total;
}

/** The priced lines of a Price Estimate: children, or the category row itself when it has none. */
function priceEstimateLines(rows) {
  const lines = [];
  (rows || []).forEach((pe) => {
    if (pe.children && pe.children.length) pe.children.forEach((c) => lines.push(c));
    else lines.push(pe);
  });
  return lines;
}

/**
 * Display names of every line that cannot be priced yet (no rate, no quantity, so no total).
 * These render as "Rate needed" rows and block the grand total.
 */
export function listUnpricedLines(rows) {
  return priceEstimateLines(rows)
    .filter((l) => isBlankMoney(l.totalPrice) || isBlankMoney(l.unitPrice))
    .map((l) => String(l.expenseHead || "").trim() || "(unnamed line)");
}

/**
 * The grand total, or null when it cannot be stated honestly: no lines at all, or at least one
 * line is still unpriced. A partial sum is never presented as "the total".
 */
export function computePriceEstimateGrandTotalOrNull(rows) {
  const lines = priceEstimateLines(rows);
  if (lines.length === 0) return null;
  if (listUnpricedLines(rows).length > 0) return null;
  return computePriceEstimateGrandTotal(rows);
}

/**
 * Re-derives every Payment Tranche's `payableAmount` from its `pctOfTotal` share of
 * `grandTotalInr`, so the Payment Tranches footer total always equals the Price Estimate
 * grand total exactly — the last tranche absorbs the rounding remainder instead of each
 * row independently rounding its own share (which can drift the sum off by a rupee or two).
 */
export function recomputeTranchePayables(tranches, grandTotalInr) {
  const rows = tranches || [];
  if (!rows.length) return rows;
  // No honest grand total (null/undefined/NaN: some line is unpriced) => no tranche amount.
  const hasTotal = grandTotalInr !== null && grandTotalInr !== undefined && Number.isFinite(Number(grandTotalInr));
  const pctOf = (t) => {
    if (isBlankMoney(t.pctOfTotal)) return null;
    const p = parseFloat(String(t.pctOfTotal).replace(/[^0-9.]/g, ""));
    return Number.isFinite(p) ? p : null;
  };
  if (!hasTotal) return rows.map((t) => ({ ...t, payableAmount: "" }));
  const target = Math.round(Number(grandTotalInr));
  // The last-row remainder plug only applies when every share is stated; a blank share has no amount.
  const allPct = rows.every((t) => pctOf(t) !== null);
  let allocated = 0;
  return rows.map((t, idx) => {
    const pct = pctOf(t);
    if (pct === null) return { ...t, payableAmount: "" };
    const isLast = allPct && idx === rows.length - 1;
    const amount = isLast ? target - allocated : Math.round((target * pct) / 100);
    if (!isLast) allocated += amount;
    return { ...t, payableAmount: formatInrForStorage(amount) };
  });
}

/** One contract-summary line for a price row: 'Rate needed' when it has no rate, never 'x  =  0'. */
export function formatPriceLine(line, indent = "") {
  const head = String(line.expenseHead || "").trim() || "(unnamed line)";
  if (isBlankMoney(line.unitPrice) || isBlankMoney(line.totalPrice)) return `${indent}${head}: Rate needed`;
  return `${indent}${head}: ${line.totalUnits} ${line.unitOfMeasurement} × ${line.unitPrice} = ${line.totalPrice}`;
}

/** One contract-summary line for a payment tranche; blank share/amount are stated as not yet set, never "0%". */
export function formatTrancheLine(t) {
  const share = isBlankMoney(t.pctOfTotal) ? "share not set" : String(t.pctOfTotal);
  const amount = isBlankMoney(t.payableAmount) ? "" : ` (${t.payableAmount})`;
  return `${t.particulars}: ${t.description} - ${share}${amount}`;
}
